import { jsx as _jsx } from "react/jsx-runtime";
import { DiscussionsComment } from "@air/component-discussions";
import { formatFullName, formatUpdatedAt, parseAirDateToISO } from "@air/utilities";
import { useCurrentUserSession } from "@air/utils-auth";
import { memo, useCallback, useMemo } from "react";
import { CommentAvatar } from "~/components/Discussions/Discussion/DiscussionComment/components/CommentAvatar";
import { CommentMenu } from "~/components/Discussions/Discussion/DiscussionComment/components/CommentMenu";
import { DiscussionCommentContent } from "~/components/Discussions/Discussion/DiscussionComment/components/DiscussionCommentContent";
import { DiscussionVersionNumber } from "~/components/Discussions/Discussion/DiscussionComment/components/DiscussionVersionNumber";
import { ReopenDiscussionButton } from "~/components/Discussions/Discussion/DiscussionComment/components/ReopenDiscussionButton";
import { useCommentEditing } from "~/components/Discussions/Discussion/shared/useCommentEditing";
import { useDiscussionPermissions } from "~/components/Discussions/providers/DiscussionPermissionsProvider";
import { useAccountContext } from "~/providers/AccountProvider";
import { isCommentAuthor } from "~/utils/comments";
import { canEditAssetDiscussions } from "~/utils/permissions/assetPermissions";
export const DiscussionComment = /*#__PURE__*/ memo((param)=>{
    let { discussion, className, comment, onSwitchVersion, mentionsList, shouldShowMentionsButton, style, currentVersion, onSave, onDelete, onToggleResolve, commentMetadata, secondaryActions } = param;
    const { disableEditing, enableEditing, isEditing } = useCommentEditing();
    const { data: account } = useAccountContext();
    const { data: userSession } = useCurrentUserSession();
    const isTopLevelComment = discussion.comments.data[0].id === comment.id;
    const isAuthor = isCommentAuthor({
        comment,
        account,
        userSession
    });
    const { permissions } = useDiscussionPermissions();
    const canResolve = isTopLevelComment && onToggleResolve && canEditAssetDiscussions(permissions);
    const canEdit = onSave && isAuthor && canEditAssetDiscussions(permissions);
    const canDelete = onDelete && isAuthor && canEditAssetDiscussions(permissions);
    const commentMenu = useMemo(()=>discussion.resolved && isTopLevelComment && canResolve ? /*#__PURE__*/ _jsx(ReopenDiscussionButton, {
            onToggleResolve: onToggleResolve,
            discussion: discussion
        }) : /*#__PURE__*/ _jsx(CommentMenu, {
            comment: comment,
            discussion: discussion,
            onToggleResolve: canResolve ? onToggleResolve : undefined,
            onDelete: canDelete ? onDelete : undefined,
            onEdit: canEdit ? enableEditing : undefined
        }), [
        discussion,
        isTopLevelComment,
        canResolve,
        onToggleResolve,
        comment,
        canDelete,
        onDelete,
        canEdit,
        enableEditing
    ]);
    const commentAvatar = useMemo(()=>/*#__PURE__*/ _jsx(CommentAvatar, {
            account: comment.account,
            isResolved: discussion.resolved
        }), [
        comment.account,
        discussion.resolved
    ]);
    const onSaveClick = useCallback((newComment)=>{
        onSave === null || onSave === void 0 ? void 0 : onSave({
            newComment,
            comment,
            clipId: discussion.clipId,
            timestamp: discussion.timestamp,
            annotation: discussion.annotation
        });
    }, [
        onSave,
        comment,
        discussion
    ]);
    const headerVersion = useMemo(()=>onSwitchVersion && isTopLevelComment && discussion.assetVersion ? /*#__PURE__*/ _jsx(DiscussionVersionNumber, {
            discussionVersion: discussion.assetVersion,
            onVersionClick: discussion.assetVersion !== currentVersion ? ()=>{
                onSwitchVersion === null || onSwitchVersion === void 0 ? void 0 : onSwitchVersion(discussion.clipId);
            } : undefined
        }) : null, [
        currentVersion,
        discussion.assetVersion,
        discussion.clipId,
        isTopLevelComment,
        onSwitchVersion
    ]);
    return /*#__PURE__*/ _jsx(DiscussionsComment, {
        actions: commentMenu,
        author: formatFullName(comment.account.firstName, comment.account.lastName),
        avatar: commentAvatar,
        className: className,
        comment: /*#__PURE__*/ _jsx(DiscussionCommentContent, {
            isEditing: isEditing,
            cancelEditing: disableEditing,
            comment: comment,
            onSave: canEdit ? onSaveClick : undefined,
            mentionsList: mentionsList,
            shouldShowMentionsButton: shouldShowMentionsButton,
            metadata: commentMetadata
        }, comment.id),
        "data-comment-id": comment.id,
        meta: headerVersion,
        secondaryActions: secondaryActions,
        style: style,
        timestamp: formatUpdatedAt(parseAirDateToISO(comment.createdAt).getTime())
    });
});
DiscussionComment.displayName = "DiscussionComment";
